import SimplePanel from '@/shared/components/SimplePanel';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import Checkbox from '@/shared/components/input/Checkbox';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

import Axios from 'axios';
import { getResourceName } from '@/shared/components/ResourceName';
import { injectStateService, StateService } from '@/shared/services/StateService';
import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import moment from 'moment';
import { CanCreate, showWhenPermission } from '@/shared/components/security';
import ToastrService from '@/shared/services/ToastrService';
import SignalRService from '@/shared/services/SignalRService';
import { functionalities, privileges } from '@/shared/services/AuthService';
import intlShape from '@/shared/utils/intlShape';
import messages from '../hrmMessages';
import HrmOverviewRow from './HrmOverviewRow';
import HrmOverviewFilters, { defaultFilters } from './HrmOverviewFilters';
import GlobalActionsButton from './GlobalActionButton';
import HrmModals from './HrmModals';

class HrmOverviewContainer extends Component {
  constructor(props) {
    super(props);
    const { searchParams = {}, intl } = props;

    const { hrmFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem('HrmOverviewParams')) || {};

    this.state = {
      items: [],
      currentSearchParams: undefined,
      allSelected: false,
      inDays: true,
      initialSearchParams: {
        // Set default filters
        sortKey: 'displayname',
        pageSize: -1,
        hrmFilters: {
          ...defaultFilters,
          ...sessionFilters,
          ...searchParams,
        },
        // Override with session storage params
        ...sessionParams,
      },
    };
  }
  componentDidMount() {
    SignalRService.subscribe('jobUpdated', this.jobUpdateHandler);
  }
  componentWillUnmount() {
    SignalRService.unsubscribe('jobUpdated', this.jobUpdateHandler);
    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to leaving page.');
    }
  }

  getYearFromFilters = () => {
    const { initialSearchParams: { hrmFilters: { year } = {} } = {} } = this.state;
    return year;
  };
  loadItems = searchParams => {
    sessionStorage.setItem(
      'HrmOverviewParams',
      JSON.stringify({
        desc: searchParams.desc,
        sortKey: searchParams.sortKey,
        hrmFilters: searchParams.hrmFilters,
      }),
    );
    const { allSelected } = this.state;
    const { intl } = this.props;
    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to new request.');
    }
    this.searchToken = Axios.CancelToken.source();
    return api.base
      .get('/api/AbsenceCounter/Overview', {
        params: {
          desc: searchParams.desc,
          sort: searchParams.sortKey,
          ...searchParams.hrmFilters,
        },
        cancelToken: this.searchToken.token,
      })
      .then(({ data }) => {
        // If checkbox for all relations is selected, set selected on all new incoming relations
        const items = data.rows.map(e => ({ ...e, selected: !!allSelected }));
        const absences = data.absences.map(e => ({ ...e, label: getResourceName(intl, e) }));

        this.setState(state => ({
          ...state,
          items,
          absences,
          currentSearchParams: searchParams,
        }));

        return items;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
        // Return empty array for cancelled requests so ScrollableTable can handle it properly
        return [];
      });
  };
  filtersChanged = filters => {
    this.setState(state => ({
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        hrmFilters: filters,
      },
    }));
  };

  handleSelect = row => {
    this.setState(({ items }) => ({
      items: items.map(x =>
        x.employeeID === row.employeeID ? { ...x, selected: !x.selected } : x,
      ),
    }));
  };
  handleSelectAll = () => {
    this.setState(({ items, allSelected }) => ({
      items: items.map(x => ({ ...x, selected: !allSelected })),
      allSelected: !allSelected,
    }));
  };

  handleUpdateCounter = (employeeId, resourceId, counter, callback = undefined) => {
    this.setState(
      ({ items }) => ({
        items: items.map(r =>
          r.employeeID !== employeeId
            ? r
            : {
                ...r,
                counters: {
                  ...r.counters,
                  [resourceId]: counter,
                },
              },
        ),
      }),
      callback,
    );
  };

  handleInitCounter = counter => {
    const { resourceId, employeeId, startValueWholeDays, startValueRemainderMinutes } =
      counter || {};
    if (!resourceId || !employeeId) return Promise.reject();
    if ((startValueWholeDays ?? 0) === 0 && (startValueRemainderMinutes ?? 0) === 0)
      return Promise.reject();
    const year = this.getYearFromFilters();
    const { intl } = this.props;
    return new Promise((resolve, reject) => {
      api.base
        .post('/api/AbsenceCounter/Init', {
          employeeId,
          startValueWholeDays,
          startValueRemainderMinutes,
          resourceId,
          year,
        })
        .then(({ data }) => {
          if (!data) return reject();
          if (data.year !== year) return reject();
          this.handleUpdateCounter(
            employeeId,
            resourceId,
            {
              id: data.id,
              startValue: data.startValue,
              startValueWholeDays: data.startValueWholeDays,
              startValueRemainderMinutes: data.startValueRemainderMinutes,
              balance: data.balance,
              balanceWholeDays: data.balanceWholeDays,
              balanceRemainderMinutes: data.balanceRemainderMinutes,
            },
            resolve,
          );
        })
        .catch(error => {
          handleHttpError(intl)(error);
          return reject();
        });
    });
  };

  handleDeleteCounter = ({ id, employeeId, resourceId }) => {
    const { intl } = this.props;
    api.base
      .delete(`/api/AbsenceCounter/${id}`)
      .then(() => {
        this.handleUpdateCounter(employeeId, resourceId, null);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };
  handleOpenRegOverview = ({ userId, resourceId = null }) => {
    const { stateService } = this.props;
    const year = this.getYearFromFilters();

    stateService.gotoRegistrationOverview({
      userId,
      period: 'all',
      dateFrom: moment.utc([year, 0, 1]).toDate(),
      dateUntil: moment.utc([year, 11, 31]),
      resourceId,
      filters: { type: { items: [3] } },
    });
  };

  openModal = (modalName, data) => {
    this.setState({ showModal: { name: modalName, data } });
  };
  closeModals = (reload = false) => {
    if (reload) {
      this.setState(state => ({
        initialSearchParams: {
          ...state.initialSearchParams,
          page: 0,
          f: new Date().getTime(),
        },
        showModal: null,
      }));
      return;
    }
    this.setState({ showModal: null });
  };
  isGlobalActionsDisabled = () => {
    const { items, allSelected } = this.state;
    if (!items || !items.length) return true;
    if (allSelected || items.some(x => x.selected)) return false;

    return true;
  };
  getSelectionParams = () => {
    const { items, allSelected } = this.state;

    const ids = items
      ? items.filter(x => (x.selected || false) !== allSelected).map(x => x.employeeID)
      : [];

    return {
      ids,
      selectAll: allSelected,
    };
  };

  handleGlobalActionJobResult = ({ data }) => {
    const { stateService, intl } = this.props;
    const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

    ToastrService.info(intl.formatMessage(messages.BackgroundJobQueued), undefined, onClick);
    this.closeModals();
  };

  initCounterGlobalAction = settings => {
    const { intl } = this.props;
    const { initialSearchParams } = this.state;
    const bodyParams = {
      ...settings,
      ...this.getSelectionParams(),
    };
    return api.base
      .post(`/api/AbsenceCounter/BatchInit`, bodyParams, {
        params: initialSearchParams.hrmFilters,
      })
      .then(this.handleGlobalActionJobResult)
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };
  jobUpdateHandler = job => {
    if (job.jobType === 53 && (job.status === 2 || job.status === 3)) {
      this.setState(state => ({
        initialSearchParams: {
          ...state.initialSearchParams,
          page: 0,
          // forces refresh of table
          f: new Date().getTime(),
        },
      }));
    }
  };

  render() {
    const { intl, stateService } = this.props;
    const { items, absences, allSelected, initialSearchParams, showModal, inDays } = this.state;
    const year = this.getYearFromFilters();
    const resourceId = initialSearchParams?.hrmFilters?.resourceId;
    return (
      <div className="hrm-overview">
        <SimplePanel
          title={
            <span>
              {intl.formatMessage(messages.AbsenceCounters)}{' '}
              <ZendeskHelp article="13956867188764" className="margin10R" />
            </span>
          }
        >
          <div className="info-block container-fluid radius4B">
            <HrmOverviewFilters
              filters={initialSearchParams.hrmFilters}
              onFilterChange={this.filtersChanged}
              inDays={inDays}
              inDaysChanged={x => this.setState({ inDays: !!x })}
              intl={intl}
            />
          </div>
        </SimplePanel>
        <div className="hidden-xs">
          <div className="pull-right content-right">
            <CanCreate on="AbsenceAndCounters">
              <GlobalActionsButton
                onInitCounter={() =>
                  this.openModal('initCounter', {
                    resourceId,
                    year,
                    inDays,
                    save: this.initCounterGlobalAction,
                  })
                }
                intl={intl}
                disabled={this.isGlobalActionsDisabled()}
              />
            </CanCreate>
          </div>
        </div>

        <ScrollableTable
          tableClassName="table-grid"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => (
            <HrmOverviewRow
              key={item.employeeID}
              intl={intl}
              absences={absences}
              data={item}
              handleSelect={this.handleSelect}
              onInitCounter={this.handleInitCounter}
              onDeleteCounter={this.handleDeleteCounter}
              onOpenRegistrationOverview={this.handleOpenRegOverview}
              onOpenCounterDetails={data =>
                this.openModal('counterDetails', { counter: data, hoursInDay: item.hoursInDay })
              }
              inDays={inDays}
              stateService={stateService}
            />
          )}
        >
          <ColumnHeader
            className="checkbox-column"
            title={
              <Checkbox
                extraClass="margin2L"
                onClick={() => this.handleSelectAll()}
                value={allSelected}
              />
            }
          />

          <ColumnHeader title={intl.formatMessage(messages.Name)} />

          {absences?.map(column => (
            <ColumnHeader key={column.id} title={column.label} />
          ))}

          <ColumnHeader
            title={intl.formatMessage(messages.Actions)}
            id="hrmOverviewActions"
            className="text-right"
          />
        </ScrollableTable>

        <HrmModals
          modal={showModal?.name}
          data={showModal?.data}
          intl={intl}
          onCloseModals={this.closeModals}
        />
      </div>
    );
  }
}
HrmOverviewContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  searchParams: PropTypes.shape({}),
};
HrmOverviewContainer.defaultProps = {
  searchParams: {},
};

export default showWhenPermission(
  functionalities.AbsenceAndCounters,
  privileges.Read,
)(injectStateService(injectIntl(HrmOverviewContainer)));
